import { DateRangeSearch } from '@routes/__root'
import { environmentIdsSearchSchema } from '@routes/-environmentIdsSearch'
import { protectWithWorkspacePermissions } from '@routes/-protectWithPermission'
import { withCompanyName } from '@routes/-withCompanyName'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { SubscriptionOverviewPage } from 'features/subscriptionOverview'
import { withCachedSearch } from 'helpers/withCachedSearch'
import { subscriptionQueryOptions } from 'hooks/api/subscriptions'
import { canViewAnalyticsPermission } from 'hooks/usePermission'
import { ChartValueMode, OverviewChartValues } from 'models'
import { z } from 'zod/v4'

const cachedSearchParams = ['period', 'before', 'since'] as const

type CachedKeys = (typeof cachedSearchParams)[number]
type CachedSubset = Pick<DateRangeSearch, CachedKeys>

const searchSchema = z.object({
  ...environmentIdsSearchSchema.shape,
  chart: z.enum(OverviewChartValues).optional().catch(undefined),
  valueMode: z.enum(ChartValueMode).optional().catch(undefined),
})

export const Route = createFileRoute('/_authenticated/_layout/workspaces/$subscriptionId/overview')({
  component: SubscriptionOverviewPage,
  validateSearch: searchSchema,
  loader: ({ context: { queryClient, buildRequest }, params: { subscriptionId } }) => {
    queryClient.prefetchQuery(subscriptionQueryOptions(buildRequest, subscriptionId, ['usageQuota']))
  },
  head: () => ({
    meta: [
      {
        title: withCompanyName('Overview - Workspace'),
      },
    ],
  }),
  beforeLoad: async (ctx) => {
    await protectWithWorkspacePermissions(canViewAnalyticsPermission)(ctx)

    const { search, location, params, cause } = ctx
    if (cause !== 'preload') {
      const cachedSearch = withCachedSearch<CachedSubset>({
        search,
        matchedRoute: location.pathname,
        cachedParamsWhitelist: cachedSearchParams,
        searchStr: location.searchStr,
      })

      if (cachedSearch) {
        throw redirect({
          to: '/workspaces/$subscriptionId/overview',
          params,
          replace: true,
          search: (old) => ({
            ...old,
            ...cachedSearch,
          }),
        })
      }
    }
  },
})
